import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import LytButton from 'src/components/library/button/LytButton';
import LytTextField from 'src/components/library/text-field/LytTextField';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from 'src/components/styled/theme/typography-utils';
import LytModal from 'src/components/ui-core/modal/LytModal';

const FORM_ID = 'cad-avl-assignment-form';
const TITLE_ID = 'cad-avl-assignment-modal-title';
const DESCRIPTION_ID = 'cad-avl-assignment-modal-description';

const ModalContent = styled.form`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const RequiredNote = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
`;

const RequiredAsterisk = styled.span`
    color: ${({ theme }) => theme?.text?.danger?.default};
`;

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const ButtonGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const getAssignmentValue = (assignment, snakeCaseKey, camelCaseKey) =>
    assignment?.[snakeCaseKey] ?? assignment?.[camelCaseKey] ?? '';

/**
 * Shared create/edit form for CAD/AVL apparatus-to-unit assignments.
 *
 * This modal intentionally returns the entered values to its caller without
 * performing persistence. Endpoint integration is owned by a follow-up ticket.
 */
const CadAvlAssignmentModal = ({
    open = false,
    closeModal = undefined,
    mode = 'create',
    assignment = {},
}) => {
    const isEdit = mode === 'edit';
    const initialApparatusId = isEdit
        ? getAssignmentValue(assignment, 'apparatus_id', 'apparatusId')
        : '';
    const initialUnitId = isEdit
        ? getAssignmentValue(assignment, 'unit_id', 'unitId')
        : '';
    const [apparatusId, setApparatusId] = useState(initialApparatusId);
    const [unitId, setUnitId] = useState(initialUnitId);

    if (!open) {
        return null;
    }

    const trimmedApparatusId = apparatusId.trim();
    const trimmedUnitId = unitId.trim();
    const hasChanges =
        trimmedApparatusId !== initialApparatusId.trim() ||
        trimmedUnitId !== initialUnitId.trim();
    const submitDisabled =
        !trimmedApparatusId || !trimmedUnitId || (isEdit && !hasChanges);

    const handleClose = (result = {}) => {
        closeModal?.({
            confirmed: false,
            ...result,
        });
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (submitDisabled) {
            return;
        }

        closeModal?.({
            confirmed: true,
            mode,
            assignment: {
                ...assignment,
                apparatus_id: trimmedApparatusId,
                unit_id: trimmedUnitId,
            },
        });
    };

    const footerActions = (
        <ButtonGroup>
            <LytButton
                type="tertiary"
                onClick={() => handleClose({ reason: 'cancel' })}>
                Close
            </LytButton>
            <LytButton
                type="primary"
                buttonType="submit"
                form={FORM_ID}
                disabled={submitDisabled}>
                Submit
            </LytButton>
        </ButtonGroup>
    );

    return (
        <LytModal
            open={open}
            title={isEdit ? 'Edit New Assignment' : 'Add New Assignment'}
            onClose={handleClose}
            actions={footerActions}
            size="medium"
            ariaLabelledBy={TITLE_ID}
            ariaDescribedBy={DESCRIPTION_ID}>
            <ModalContent id={FORM_ID} onSubmit={handleSubmit}>
                <RequiredNote id={DESCRIPTION_ID}>
                    Required fields are marked <RequiredAsterisk>*</RequiredAsterisk>
                </RequiredNote>
                <Fields>
                    <LytTextField
                        name="cad-avl-apparatus-id"
                        type="text"
                        label="Apparatus ID"
                        initialValue={apparatusId}
                        onChange={setApparatusId}
                        helperText="Apparatus ID is the ID for the vehicle such as a fire truck or ambulance."
                        required
                        aria-label="Apparatus ID"
                    />
                    <LytTextField
                        name="cad-avl-unit-id"
                        type="text"
                        label="Unit ID"
                        initialValue={unitId}
                        onChange={setUnitId}
                        helperText="Unit ID is the emergency team of people responding to an incident."
                        required
                        aria-label="Unit ID"
                    />
                </Fields>
            </ModalContent>
        </LytModal>
    );
};

CadAvlAssignmentModal.propTypes = {
    open: PropTypes.bool,
    closeModal: PropTypes.func,
    mode: PropTypes.oneOf(['create', 'edit']),
    assignment: PropTypes.shape({
        mapping_id: PropTypes.string,
        apparatus_id: PropTypes.string,
        unit_id: PropTypes.string,
        apparatusId: PropTypes.string,
        unitId: PropTypes.string,
    }),
};

export default CadAvlAssignmentModal;
